import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Calculator, 
  Moon, 
  Calendar, 
  DollarSign,
  Award,
  TrendingUp,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { agentsAPI, shiftsAPI, paySlipsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const SalaryCalculator = () => {
  const { toast } = useToast();
  const [agents, setAgents] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  const [formData, setFormData] = useState({
    selectedAgent: '',
    poste: '',
    coefficient: 140,
    heures: 151.67,
    heuresNuit: 0,
    heuresDimanche: 0,
    heuresFerie: 0,
    joursTravailles: 20,
    joursRepas: 0,
    primeHabillage: true,
    primePanier: false,
    primeSSIAP: false,
    primeChef: false,
    primeRisque: false,
    nbChiens: 1,
    navigo: true,
    entretien: true
  });

  const [results, setResults] = useState({
    salaireBase: 0,
    totalPrimes: 0,
    totalMajorations: 0,
    salaireBrut: 0,
    cotisations: 0,
    cotisationsDetails: {},
    indemnites: 0,
    netImposable: 0,
    netAPayer: 0
  });

  // Taux horaires par coefficient (Convention Collective 1351 - 2025)
  // Hausse de 3,2% au 1er janvier 2025
  const tauxHoraires = {
    120: 12.082,  // Agent de sécurité qualifié
    130: 12.241,  // Agent de sécurité confirmé
    140: 12.608,  // Agent chef de poste, mobile, cynophile, SSIAP 1
    150: 13.080,  // Agent filtrage, aéroportuaire, nucléaire
    160: 13.803,  // Profileur, opérateur qualifié
    175: 14.925,  // Opérateur confirmé
    190: 16.047,  // Coordinateur
    210: 17.548,  // Niveau 5 échelon 1
    230: 19.0445, // Niveau 5 échelon 2
    250: 20.5415, // Niveau 5 échelon 3
    // Agent de maîtrise
    AM150: 14.330, // Chef d'équipe SSIAP 2
    AM160: 15.122,
    AM170: 15.912,
    AM185: 17.101, // Pompier chef manœuvre
    AM200: 18.287, // Chef d'équipe
    AM215: 19.473,
    AM235: 21.055, // SSIAP 3, chef site nucléaire
    AM255: 22.638, // Superviseur
    AM275: 24.220
  };

  // Primes (Convention 1351 - 2025)
  const primesMontants = {
    habillageHeure: 0.1307, // Prime d'habillage: 0,1307€ par heure travaillée
    ssiap1: 50,
    ssiap2: 100,
    ssiap3: 200,
    cynophile: 150,
    primeChien: 1.37, // Par heure de travail effectif (2025)
    chefEquipe: 75,
    chefPoste: 100,
    responsabilite: 150,
    risque: 100,
    entretienTenue: 8.54,  // 2025
    panierJour: 4.36,      // 2025
    panierAeroportuaire: 6.68, // Annexe VIII
    navigoPartiel: 44.40
  };

  const postes = [
    // I. Agent d'exploitation (Coef 120-210)
    { value: 'agent_qualifie', label: 'Agent de sécurité qualifié (Coef 120)', coef: 120 },
    { value: 'agent_confirme', label: 'Agent de sécurité confirmé (Coef 130)', coef: 130 },
    { value: 'agent_magasin', label: 'Agent magasin prévention vols (Coef 130)', coef: 130 },
    { value: 'agent_chef_poste', label: 'Agent chef de poste (Coef 140)', coef: 140 },
    { value: 'agent_mobile', label: 'Agent mobile (Coef 140)', coef: 140 },
    { value: 'maitre_chien', label: 'Agent cynophile (Coef 140)', coef: 140 },
    { value: 'ssiap1', label: 'Agent SSIAP 1 (Coef 140)', coef: 140 },
    { value: 'agent_filtrage', label: 'Agent filtrage (Coef 150)', coef: 150 },
    { value: 'agent_aeroportuaire', label: 'Agent sûreté aéroportuaire (Coef 150)', coef: 150 },
    { value: 'agent_nucleaire', label: 'Agent sécurité nucléaire (Coef 150)', coef: 150 },
    { value: 'profileur', label: 'Profileur (Coef 160)', coef: 160 },
    { value: 'operateur_confirme', label: 'Opérateur sûreté confirmé (Coef 175)', coef: 175 },
    { value: 'coordinateur', label: 'Coordinateur (Coef 190)', coef: 190 },
    { value: 'niveau5_e1', label: 'Niveau 5 Échelon 1 (Coef 210)', coef: 210 },
    { value: 'niveau5_e2', label: 'Niveau 5 Échelon 2 (Coef 230)', coef: 230 },
    { value: 'niveau5_e3', label: 'Niveau 5 Échelon 3 (Coef 250)', coef: 250 },
    // II. Agent de maîtrise
    { value: 'ssiap2', label: 'Chef d\'équipe SSIAP 2 (AM150)', coef: 'AM150' },
    { value: 'chef_poste_nucleaire', label: 'Chef de poste nucléaire (AM150)', coef: 'AM150' },
    { value: 'pompier_chef', label: 'Pompier chef de manœuvre (AM185)', coef: 'AM185' },
    { value: 'chef_equipe_aero', label: 'Chef d\'équipe aéroportuaire (AM200)', coef: 'AM200' },
    { value: 'ssiap3', label: 'Chef de service SSIAP 3 (AM235)', coef: 'AM235' },
    { value: 'chef_site_nucleaire', label: 'Chef de site nucléaire (AM235)', coef: 'AM235' },
    { value: 'superviseur', label: 'Superviseur (AM255)', coef: 'AM255' }
  ];

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await agentsAPI.getAll();
        setAgents(response.data || []);
      } catch (error) {
        console.error('Erreur chargement agents:', error);
      }
    };
    fetchAgents();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePosteChange = (e) => {
    const selectedPoste = postes.find(p => p.value === e.target.value);
    setFormData(prev => ({
      ...prev,
      poste: e.target.value,
      coefficient: selectedPoste ? selectedPoste.coef : 140
    }));
  };

  const calculateSalary = (e) => {
    e.preventDefault();

    // Calcul salaire de base
    const tauxHoraire = tauxHoraires[formData.coefficient];
    const salaireBase = formData.heures * tauxHoraire;

    // Calcul des primes
    let totalPrimes = 0;

    // Prime d'habillage (0,1307€ par heure travaillée)
    if (formData.primeHabillage && formData.heures > 0) {
      totalPrimes += (formData.heures * primesMontants.habillageHeure);
    }

    if (formData.primeSSIAP) {
      if (formData.poste.includes('ssiap1')) totalPrimes += primesMontants.ssiap1;
      else if (formData.poste.includes('ssiap2')) totalPrimes += primesMontants.ssiap2;
      else if (formData.poste.includes('ssiap3')) totalPrimes += primesMontants.ssiap3;
    }

    if (formData.primeChef) {
      if (formData.poste.includes('chef_poste')) totalPrimes += primesMontants.chefPoste;
      else if (formData.poste.includes('responsable')) totalPrimes += primesMontants.responsabilite;
      else totalPrimes += primesMontants.chefEquipe;
    }

    if (formData.primeRisque) {
      totalPrimes += primesMontants.risque;
    }

    // Prime de panier (repas)
    if (formData.primePanier && formData.joursRepas > 0) {
      totalPrimes += (formData.joursRepas * primesMontants.panierJour);
    }

    if (formData.poste === 'maitre_chien') {
      totalPrimes += primesMontants.cynophile;
      totalPrimes += (parseInt(formData.nbChiens) * primesMontants.primeChien * formData.heures);
    }

    // Calcul des majorations
    const majorationNuit = formData.heuresNuit * tauxHoraire * 0.10;
    const majorationDimanche = formData.heuresDimanche * tauxHoraire * 0.10;
    const majorationFerie = formData.heuresFerie * tauxHoraire * 1.00;
    const totalMajorations = majorationNuit + majorationDimanche + majorationFerie;

    // Salaire brut
    const salaireBrut = salaireBase + totalPrimes + totalMajorations;

    // Cotisations détaillées (basées sur les vrais taux des bulletins)
    const cotisationsDetails = {
      // SANTÉ
      sante_ss_maladie: salaireBrut * 0.00965, // 0.965%
      sante_complementaire_incap: salaireBrut * 0.005, // Estimation forfaitaire
      sante_complementaire_sante: salaireBrut * 0.01, // Estimation forfaitaire
      
      // RETRAITE
      retraite_ss_plafonnee: salaireBrut * 0.0690, // 6.90%
      retraite_ss_deplafonnee: salaireBrut * 0.0040, // 0.40%
      retraite_complementaire_t1: salaireBrut * 0.0401, // 4.01%
      
      // CSG/CRDS
      csg_deductible: salaireBrut * 0.0680, // 6.80%
      csg_crds_non_deductible: salaireBrut * 0.0290, // 2.90%
    };

    // Total cotisations salariales
    const cotisations = Object.values(cotisationsDetails).reduce((sum, val) => sum + val, 0);

    // Indemnités non soumises
    let indemnites = formData.joursTravailles * primesMontants.panierJour;
    if (formData.navigo) {
      indemnites += primesMontants.navigoPartiel;
    }
    if (formData.entretien) {
      indemnites += primesMontants.entretienTenue;
    }

    // Net imposable (Brut - cotisations salariales + part non déductible CSG/CRDS)
    const netImposable = salaireBrut - cotisations + cotisationsDetails.csg_crds_non_deductible;

    // Net à payer
    const netAPayer = salaireBrut - cotisations + indemnites;

    setResults({
      salaireBase: salaireBase.toFixed(2),
      totalPrimes: totalPrimes.toFixed(2),
      totalMajorations: totalMajorations.toFixed(2),
      salaireBrut: salaireBrut.toFixed(2),
      cotisations: cotisations.toFixed(2),
      cotisationsDetails: {
        sante_ss_maladie: cotisationsDetails.sante_ss_maladie.toFixed(2),
        sante_complementaire_incap: cotisationsDetails.sante_complementaire_incap.toFixed(2),
        sante_complementaire_sante: cotisationsDetails.sante_complementaire_sante.toFixed(2),
        retraite_ss_plafonnee: cotisationsDetails.retraite_ss_plafonnee.toFixed(2),
        retraite_ss_deplafonnee: cotisationsDetails.retraite_ss_deplafonnee.toFixed(2),
        retraite_complementaire_t1: cotisationsDetails.retraite_complementaire_t1.toFixed(2),
        csg_deductible: cotisationsDetails.csg_deductible.toFixed(2),
        csg_crds_non_deductible: cotisationsDetails.csg_crds_non_deductible.toFixed(2),
      },
      indemnites: indemnites.toFixed(2),
      netImposable: netImposable.toFixed(2),
      netAPayer: netAPayer.toFixed(2)
    });

    setShowResults(true);

    toast({
      title: "Calcul effectué",
      description: `Salaire net: ${netAPayer.toFixed(2)}€`,
    });
  };

  const handleGeneratePaySlip = async () => {
    try {
      if (!formData.poste || !formData.heures) {
        toast({ title: 'Erreur', description: 'Renseignez au moins le poste et les heures', variant: 'destructive' });
        return;
      }
      const selectedPoste = postes.find((p) => p.value === formData.poste);
      const payload = {
        agentName: agents.find((a) => a.id === formData.selectedAgent)?.fullName || 'Agent Sécurité',
        posteLabel: selectedPoste ? selectedPoste.label : 'Agent de sécurité',
        coefficient: String(formData.coefficient),
        periode: new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
        heures: Number(formData.heures),
        salaireBase: Number(results.salaireBase || 0),
        totalPrimes: Number(results.totalPrimes || 0),
        totalMajorations: Number(results.totalMajorations || 0),
        salaireBrut: Number(results.salaireBrut || 0),
        cotisations: Number(results.cotisations || 0),
        indemnites: Number(results.indemnites || 0),
        netImposable: Number(results.netImposable || 0),
        netAPayer: Number(results.netAPayer || 0),
      };

      const response = await paySlipsAPI.generatePDF(payload);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiche_paie_${payload.agentName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Fiche de paie générée', description: 'Le PDF a été téléchargé.' });
    } catch (e) {
      console.error('Erreur génération fiche de paie:', e);
      toast({ title: 'Erreur', description: 'Impossible de générer la fiche de paie', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Calculator className="w-12 h-12 text-blue-600 mr-3" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            CALCUL-SECU
          </h1>
        </div>
        <p className="text-xl text-gray-600">
          Calculateur Paie Sécurité Privée - Convention 1351
        </p>
      </div>

      {/* Formulaire */}
      <Card>
        <CardHeader>
          <CardTitle>Calculer le salaire d&apos;un agent</CardTitle>
          <CardDescription>
            Remplissez les informations pour calculer automatiquement le salaire
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={calculateSalary}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Colonne 1 */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-blue-600" />
                    Poste et Classification
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="poste">Sélectionnez le poste</Label>
                      <select
                        id="poste"
                        name="poste"
                        value={formData.poste}
                        onChange={handlePosteChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">-- Choisir un poste --</option>
                        {postes.map(poste => (
                          <option key={poste.value} value={poste.value}>
                            {poste.label} ({poste.coef})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="heures">Heures mensuelles</Label>
                      <Input
                        type="number"
                        id="heures"
                        name="heures"
                        value={formData.heures}
                        onChange={handleInputChange}
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Moon className="w-5 h-5 mr-2 text-purple-600" />
                    Majorations
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="heuresNuit">
                        Heures de nuit (21h-6h) 
                        <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">+10%</span>
                      </Label>
                      <Input
                        type="number"
                        id="heuresNuit"
                        name="heuresNuit"
                        value={formData.heuresNuit}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="heuresDimanche">
                        Heures dimanche
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">+10%</span>
                      </Label>
                      <Input
                        type="number"
                        id="heuresDimanche"
                        name="heuresDimanche"
                        value={formData.heuresDimanche}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="heuresFerie">
                        Heures jours fériés
                        <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">+100%</span>
                      </Label>
                      <Input
                        type="number"
                        id="heuresFerie"
                        name="heuresFerie"
                        value={formData.heuresFerie}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne 2 */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                    Primes spécifiques
                  </h3>
                  
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="primeHabillage"
                        checked={formData.primeHabillage}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span>Prime habillage (0.1307€/heure)</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="primeSSIAP"
                        checked={formData.primeSSIAP}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span>Prime SSIAP</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="primeChef"
                        checked={formData.primeChef}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span>Prime encadrement</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="primeRisque"
                        checked={formData.primeRisque}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span>Prime de risque</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="primePanier"
                        checked={formData.primePanier}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span>Prime de panier/repas (4.36€/jour)</span>
                    </label>

                    {formData.primePanier && (
                      <div className="ml-6 space-y-2 mt-2">
                        <Label htmlFor="joursRepas">Nombre de jours avec repas</Label>
                        <Input
                          type="number"
                          id="joursRepas"
                          name="joursRepas"
                          value={formData.joursRepas}
                          onChange={handleInputChange}
                          min="0"
                          max="31"
                          placeholder="Ex: 20"
                        />
                        <small className="text-gray-600">
                          Total prime: {(formData.joursRepas * primesMontants.panierJour).toFixed(2)}€
                        </small>
                      </div>
                    )}

                    {formData.poste === 'maitre_chien' && (
                      <div className="ml-6 space-y-2 mt-3">
                        <Label htmlFor="nbChiens">Nombre de chiens</Label>
                        <Input
                          type="number"
                          id="nbChiens"
                          name="nbChiens"
                          value={formData.nbChiens}
                          onChange={handleInputChange}
                          min="1"
                          max="3"
                        />
                        <small className="text-gray-600">Prime: 150€ + 1.37€/heure/chien</small>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                    Autres éléments
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="joursTravailles">Jours travaillés (pour panier)</Label>
                      <Input
                        type="number"
                        id="joursTravailles"
                        name="joursTravailles"
                        value={formData.joursTravailles}
                        onChange={handleInputChange}
                        min="0"
                        max="31"
                      />
                    </div>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="navigo"
                        checked={formData.navigo}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span>Navigo (44.40€)</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="entretien"
                        checked={formData.entretien}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <span>Entretien tenues (8.54€)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-8 gap-4">
              <div className="flex gap-3">
                <Button type="submit" className="flex items-center space-x-2 px-6 py-3 text-base">
                  <Calculator className="w-5 h-5" />
                  <span>Lancer le calcul</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex items-center space-x-2 px-6 py-3 text-base"
                  onClick={handleGeneratePaySlip}
                  disabled={!showResults}
                >
                  <DollarSign className="w-5 h-5" />
                  <span>Générer la fiche de paie (PDF)</span>
                </Button>
              </div>
              <p className="text-sm text-gray-500 flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-yellow-500" />
                <span>Les taux et cotisations sont fournis à titre indicatif et doivent être validés par votre expert-comptable.</span>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Résultats */}
      {showResults && (
        <Card className="border-2 border-blue-500 animate-in slide-in-from-bottom">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardTitle className="flex items-center">
              <TrendingUp className="w-6 h-6 mr-2 text-blue-600" />
              Résultat du calcul
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Salaire de base</div>
                <div className="text-2xl font-bold text-gray-900">{results.salaireBase}€</div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Total primes</div>
                <div className="text-2xl font-bold text-green-700">{results.totalPrimes}€</div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Majorations</div>
                <div className="text-2xl font-bold text-purple-700">{results.totalMajorations}€</div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Salaire brut</div>
                <div className="text-2xl font-bold text-blue-700">{results.salaireBrut}€</div>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Cotisations totales</div>
                <div className="text-2xl font-bold text-red-700">-{results.cotisations}€</div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Indemnités</div>
                <div className="text-2xl font-bold text-orange-700">+{results.indemnites}€</div>
              </div>
            </div>

            {/* Détail des cotisations */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                📊 Détail des cotisations sociales (Convention 1351)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="bg-white p-3 rounded border-l-4 border-blue-500">
                  <div className="font-semibold text-blue-900 mb-2">💊 SANTÉ</div>
                  <div className="space-y-1 text-gray-700">
                    <div className="flex justify-between">
                      <span>Sécurité Sociale (0.965%)</span>
                      <span className="font-medium">-{results.cotisationsDetails.sante_ss_maladie}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Complémentaire Incap.</span>
                      <span className="font-medium">-{results.cotisationsDetails.sante_complementaire_incap}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Complémentaire Santé</span>
                      <span className="font-medium">-{results.cotisationsDetails.sante_complementaire_sante}€</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded border-l-4 border-purple-500">
                  <div className="font-semibold text-purple-900 mb-2">🏦 RETRAITE</div>
                  <div className="space-y-1 text-gray-700">
                    <div className="flex justify-between">
                      <span>SS Plafonnée (6.90%)</span>
                      <span className="font-medium">-{results.cotisationsDetails.retraite_ss_plafonnee}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SS Déplafonnée (0.40%)</span>
                      <span className="font-medium">-{results.cotisationsDetails.retraite_ss_deplafonnee}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Complémentaire T1 (4.01%)</span>
                      <span className="font-medium">-{results.cotisationsDetails.retraite_complementaire_t1}€</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded border-l-4 border-yellow-500 md:col-span-2">
                  <div className="font-semibold text-yellow-900 mb-2">💰 CSG / CRDS</div>
                  <div className="grid grid-cols-2 gap-3 text-gray-700">
                    <div className="flex justify-between">
                      <span>CSG Déductible (6.80%)</span>
                      <span className="font-medium">-{results.cotisationsDetails.csg_deductible}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>CSG/CRDS Non Déductible (2.90%)</span>
                      <span className="font-medium">-{results.cotisationsDetails.csg_crds_non_deductible}€</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-indigo-900">💼 Net Imposable (pour l&apos;impôt)</span>
                <span className="text-2xl font-bold text-indigo-700">{results.netImposable}€</span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg text-center">
              <div className="text-lg mb-2">NET À PAYER</div>
              <div className="text-5xl font-bold">{results.netAPayer}€</div>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-yellow-900 mb-2">
                    💡 Comparaison avec tarif comptable classique
                  </div>
                  <p className="text-sm text-yellow-800">
                    Coût actuel chez un comptable : <strong>40-60€/fiche</strong><br/>
                    Notre tarif : <strong>5€/fiche</strong>
                  </p>
                  <div className="mt-2 text-lg font-bold text-green-700">
                    💰 Économie : 45€ par fiche (90% de réduction!)
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SalaryCalculator;
